import Button from "@/components/Button";
import VideoWatermarkGenerator from "@/components/VideoWatermarkGenerator";
import useGetCoupon from "@/hooks/useGetCoupon";
import useGetUserData from "@/hooks/useGetUserData";
import useSSE from "@/hooks/useSSE";
import { CouponNotification } from "@/types";
import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import QRCode from "react-qr-code";
import { useLocation, useNavigate } from "react-router-dom";
import { trackEvent } from "@/lib/amplitude";

export default function FinishRedeem() {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const placeRef = localStorage.getItem("placeRef");
  const hasTrackedPageView = useRef(false);

  const { data, refetch, isLoading } = useGetCoupon({ placeId: placeRef });

  // Track page view
  useEffect(() => {
    if (!hasTrackedPageView.current) {
      hasTrackedPageView.current = true;
      trackEvent("coupon_page_viewed", { placeRef });
    }
  }, []);

  const { data: userData } = useGetUserData();

  // Get video from navigation state
  const originalVideo = location.state?.originalVideo as File | undefined;

  const {
    data: sseData,
    connect,
    disconnect,
  } = useSSE(
    `${import.meta.env.VITE_BACKEND_URL}/sse/coupon-notifications?user_id=${
      userData?.id
    }`,
  );

  useEffect(() => {
    if (userData) {
      connect();
      return disconnect;
    }
  }, [userData]);

  useEffect(() => {
    if (sseData) {
      const couponNotification = sseData as CouponNotification;

      if (couponNotification.restaurant_id.toString() === placeRef) {
        switch (couponNotification.event_type) {
          case "review-rejected":
            console.log("Review was rejected. navigating...");
            trackEvent("review_rejected_notification", { placeRef });
            navigate("/review-rejected");
            break;
          case "coupon-ready":
            console.log("Coupon is ready. refetching...");
            trackEvent("coupon_ready_received", { placeRef });
            refetch();
            break;
          case "coupon-redeemed":
            console.log("Coupon got redeemed. navigating...");
            trackEvent("coupon_redeemed_notification", { placeRef });
            navigate("/coupon-redeemed");
            break;
        }
      }
    }
  }, [sseData]);

  const hasCoupon = data?.coupon;

  return (
    <div className="flex min-h-screen flex-col bg-background">
      <div className="mx-auto flex min-h-[100dvh] w-full max-w-md grow flex-col px-6">
        <div className="flex grow flex-col items-center justify-center pb-20 pt-16">
          {isLoading ? (
            <div className="flex flex-col items-center gap-6">
              <div className="h-16 w-16 animate-spin rounded-full border-4 border-medium-gray border-t-primary"></div>
              <p className="text-center text-lg text-brand-gray">
                {t("common.loading")}...
              </p>
            </div>
          ) : hasCoupon ? (
            <div className="flex w-full flex-col items-center gap-8">
              <div className="flex flex-col items-center gap-3">
                <h1 className="text-center text-3xl font-bold text-foreground">
                  {t("coupon.redeem.title")}
                </h1>
                <p className="text-center text-base text-brand-gray">
                  {t("coupon.redeem.subtitle")}
                </p>
              </div>

              <div className="overflow-hidden rounded-3xl bg-medium-gray pb-0 pt-8">
                <div className="mb-6 flex flex-col items-center gap-4 px-8">
                  <QRCode
                    value={data.coupon.code}
                    className="size-full max-w-60 rounded-2xl bg-white p-4"
                  />
                </div>

                <div className="space-y-3 border-t border-light-gray bg-dark-gray/80 py-4">
                  <div className="flex items-center justify-center gap-4">
                    <span className="text-sm text-brand-gray">
                      {t("coupon.details.validUntilTime")}:
                    </span>
                    <span className="text-lg font-semibold text-foreground">
                      {new Date(data.coupon.expires_at).toLocaleTimeString("it-IT", {
                        hour: "2-digit",
                        minute: "2-digit",
                      })}
                    </span>
                  </div>
                </div>
              </div>

              <div className="mt-4 rounded-2xl border border-light-gray bg-medium-gray/50 p-4">
                <p className="text-center text-sm text-brand-gray">
                  💡 {t("coupon.redeem.expiresInfo")}
                </p>
              </div>

              {originalVideo && (
                <div className="w-full">
                  <VideoWatermarkGenerator videoFile={originalVideo} />
                </div>
              )}
            </div>
          ) : (
            <div className="flex w-full flex-col items-center gap-8">
              <div className="flex flex-col items-center gap-3">
                <h1 className="text-center text-3xl font-bold text-foreground">
                  {t("coupon.redeem.pending.title")}
                </h1>
              </div>

              <div className="w-full rounded-3xl bg-medium-gray p-8">
                <div className="mb-6 flex justify-center">
                  <div className="flex size-24 items-center justify-center rounded-full bg-amber-500/10">
                    <svg
                      className="h-16 w-16 text-amber-400"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                      />
                    </svg>
                  </div>
                </div>

                <div className="space-y-4">
                  <h2 className="text-center text-xl font-semibold text-foreground">
                    {t("coupon.redeem.pending.subtitle")}
                  </h2>
                  <p className="text-center text-sm leading-relaxed text-brand-gray">
                    {t("coupon.redeem.pending.message")}
                  </p>
                </div>

                <div className="mt-6 flex items-center gap-3 rounded-xl bg-dark-gray p-4">
                  <div className="flex h-2 w-2 animate-pulse">
                    <span className="absolute inline-flex h-2 w-2 animate-ping rounded-full bg-cyan-500 opacity-75"></span>
                    <span className="relative inline-flex h-2 w-2 rounded-full bg-cyan-500"></span>
                  </div>
                  <span className="text-sm text-brand-gray">
                    {t("coupon.redeem.pending.waiting")}
                  </span>
                </div>
              </div>

              {originalVideo && (
                <div className="w-full">
                  <VideoWatermarkGenerator videoFile={originalVideo} />
                </div>
              )}

              <Button
                onPress={() => {
                  trackEvent("app_download_clicked", {
                    placeRef,
                    source: "coupon_pending_page",
                  });
                  window.open("https://onelink.to/behnx6");
                }}
              >
                {t("conclusion.button")}
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
